/**
 * Stacking (§8, ruling S-1) -- the pure mapping between the API's
 * `StackingInput` / `PromotionDetail` stacking fields and the dialog's
 * three-way control (mode + partner ids). No I/O, no React.
 *
 * - `stacksWith` is this promotion's OWN outgoing allow-list.
 * - `stacksWithEffective` is the bilateral subset the partner reciprocates.
 * - `toStackingInput('only', [])` deliberately still emits `mode: 'only'`
 *   (the API answers 400 for an empty list); the dialog disables Save in
 *   that state rather than silently switching modes.
 */
import type { PromotionDetail, StackingInput } from '../types';

export type StackingMode = 'none' | 'any' | 'only';

export function toStackingInput(mode: StackingMode, ids: string[]): StackingInput {
  if (mode === 'none') return { mode: 'none' };
  if (mode === 'any') return { mode: 'any' };
  return { mode: 'only', promotionIds: ids };
}

export function fromDetail(
  d: Pick<PromotionDetail, 'stackable' | 'stackingMode' | 'stacksWith'>,
): { mode: StackingMode; ids: string[] } {
  if (!d.stackable) return { mode: 'none', ids: [] };
  return { mode: d.stackingMode, ids: d.stacksWith.map((p) => p.id) };
}

/** Names of own-side partners that do NOT permit this promotion back (overstated stacking). */
export function unreciprocated(d: Pick<PromotionDetail, 'stacksWith' | 'stacksWithEffective'>): string[] {
  const eff = new Set(d.stacksWithEffective.map((p) => p.id));
  return d.stacksWith.filter((p) => !eff.has(p.id)).map((p) => p.name);
}
